<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells><h1 class="title">Fast track request</h1></app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Group:</span>
              <span>{{ transfer.name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Location:</span>
              <span>{{ transfer.location.name }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="downloads">
        <div class="downloads__item" :class="{ 'downloads__item--loading': loaders.requested }">
          <div class="downloads__left">
            <div class="downloads__info">
              <span class="downloads__name downloads__name--no-events">Requested</span>
            </div>
          </div>
          <div class="downloads__right">
            <div class="downloads__values">
              <span class="downloads__key">Status:</span>
              <app-switcher
                :readonly="!is_general_manager && !is_transfer_department"
                v-model="transfer.requested"
                @change="onChangeStatus({ requested: transfer.requested }, 'requested')"
              />
            </div>
            <template v-if="transfer.requested_by">
              <div class="downloads__values">
                <span class="downloads__key">Requested at:</span>
                <span class="downloads__value downloads__width">{{ dateTimeFormat(transfer.requested_at) }}</span>
              </div>
              <div class="downloads__values">
                <span class="downloads__key">Requested by:</span>
                <div class="downloads__person downloads__width">
                  <div class="downloads__photo">
                    <img :src="getUserAvatar(transfer.requested_by?.crop.crop_url)" alt="Person">
                  </div>
                  <span class="downloads__value">{{ transfer.requested_by?.full_name || '-' }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div class="downloads__item" :class="{ 'downloads__item--loading': loaders.confirmed }">
          <div class="downloads__left">
            <div class="downloads__info">
              <span class="downloads__name downloads__name--no-events">Confirmed</span>
            </div>
          </div>
          <div class="downloads__right">
            <div class="downloads__values">
              <span class="downloads__key">Status:</span>
              <app-switcher
                :readonly="!is_general_manager && !is_transfer_department"
                v-model="transfer.confirmed"
                @change="onChangeStatus({ confirmed: transfer.confirmed }, 'confirmed')"
              />
            </div>
            <template v-if="transfer.confirmed_by">
              <div class="downloads__values">
                <span class="downloads__key">Confirmed at:</span>
                <span class="downloads__value downloads__width">{{ dateTimeFormat(transfer.confirmed_at) }}</span>
              </div>
              <div class="downloads__values">
                <span class="downloads__key">Confirmed by:</span>
                <div class="downloads__person downloads__width">
                  <div class="downloads__photo">
                    <img :src="getUserAvatar(transfer.confirmed_by?.crop.crop_url)" alt="Person">
                  </div>
                  <span class="downloads__value">{{ transfer.confirmed_by?.full_name || '-' }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div class="downloads__item" :class="{ 'downloads__item--loading': loaders.paid }">
          <div class="downloads__left">
            <div class="downloads__info">
              <span class="downloads__name downloads__name--no-events">Paid</span>
            </div>
          </div>
          <div class="downloads__right">
            <div class="downloads__values">
              <span class="downloads__key">Status:</span>
              <app-switcher
                :readonly="!is_general_manager && !is_transfer_department"
                v-model="transfer.paid"
                @change="onChangeStatus({ paid: transfer.paid }, 'paid')"
              />
            </div>
            <template v-if="transfer.paid_by">
              <div class="downloads__values">
                <span class="downloads__key">Confirmed at:</span>
                <span class="downloads__value downloads__width">{{ dateTimeFormat(transfer.paid_at) }}</span>
              </div>
              <div class="downloads__values">
                <span class="downloads__key">Confirmed by:</span>
                <div class="downloads__person downloads__width">
                  <div class="downloads__photo">
                    <img :src="getUserAvatar(transfer.paid_by?.crop.crop_url)" alt="Person">
                  </div>
                  <span class="downloads__value">{{ transfer.paid_by?.full_name || '-' }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div class="transfer" v-if="transfer.arrival_transfer">
        <h2 class="title">Arrival</h2>
        <transfers-blocks
          :transfer="transfer.arrival_transfer"
          :count="1"
          :is_show_edit_cancel_buttons="false"
          :is_show_arrange_button="false"
        />
      </div>
      <div class="transfer" v-if="transfer.departure_transfer">
        <h2 class="title">Departure</h2>
        <transfers-blocks
          :transfer="transfer.departure_transfer"
          :count="1"
          :is_show_edit_cancel_buttons="false"
          :is_show_arrange_button="false"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { getTransfersFastTrack, patchTransfersFastTrack } from '@/http'
import { format, parseISO } from 'date-fns'
import TransfersBlocks from '@/views/transfer/transfers/TransfersBlocks.vue'

export default {
  name: 'FastTrackDetail',
  components: { TransfersBlocks },
  data() {
    return {
      is_loaded: false,
      transfer: {},
      loaders: {
        requested: false,
        confirmed: false,
        paid: false,
      },
    }
  },
  created() {
    this.getTransferData()
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_transfer_department() {
      return this.$store.state.userGroups.map(item => item.code).includes('transfer_manager')
        || this.$store.state.userGroups.map(item => item.code).includes('transfer_supervisor')
        || this.$store.state.userGroups.map(item => item.code).includes('transfer_assistant')
    },
  },
  methods: {
    getTransferData() {
      getTransfersFastTrack(this.$route.params.id)
        .then(res => {
          this.transfer = res.data
          this.is_loaded = true
        })
    },
    onChangeStatus(data, field) {
      if (field) this.loaders[field] = true
      patchTransfersFastTrack(this.$route.params.id, data)
        .finally(() => { if (field) this.loaders[field] = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Data saved successfully',
          })
          this.getTransferData()
        })
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>

<style lang="sass" scoped>
.transfer
  max-width: 1100px
  margin-top: 30px

  .title
    margin-bottom: 20px
</style>